import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Check, AlertTriangle, RotateCw } from "lucide-react";
import { BRAND } from "../lib/api";
import { EASE } from "./motion";

export function Logo({ variant = "full", className = "" }) {
  const [err, setErr] = useState(false);
  if (err)
    return (
      <span className={`font-extrabold tracking-tight text-[var(--ink)] ${className}`}>
        <span className="text-[var(--brand)]">SK</span> Enstitü
      </span>
    );
  return (
    <img
      src={variant === "mark" ? "/logo-mark.png" : "/logo.png"}
      alt="SK Enstitü"
      className={`object-contain ${className}`}
      onError={() => setErr(true)}
    />
  );
}

export const FullLoader = () => (
  <div className="min-h-screen grid place-items-center bg-[var(--canvas)]" data-testid="full-loader">
    <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center gap-4">
      <Logo variant="mark" className="h-12" />
      <Loader2 className="h-5 w-5 animate-spin text-[var(--brand)]" />
    </motion.div>
  </div>
);

export function MotionButton({ children, loading, success, loadingText, variant = "primary", className = "", ...rest }) {
  const cls = variant === "primary" ? "btn-primary" : "btn-ghost";
  return (
    <button className={`${cls} inline-flex items-center justify-center gap-2 rounded-xl px-5 h-11 text-sm font-semibold ${className}`} disabled={loading || success || rest.disabled} {...rest}>
      <AnimatePresence mode="wait" initial={false}>
        {success ? (
          <motion.span key="ok" initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="inline-flex items-center gap-2">
            <Check className="h-4 w-4" /> Başarılı
          </motion.span>
        ) : loading ? (
          <motion.span key="ld" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="inline-flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> {loadingText || "Kaydediliyor..."}
          </motion.span>
        ) : (
          <motion.span key="tx" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="inline-flex items-center gap-2">
            {children}
          </motion.span>
        )}
      </AnimatePresence>
    </button>
  );
}

export const WaIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.08 1.76-.72 2-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35M12.05 21.79h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 0 1-1.51-5.26c0-5.45 4.44-9.88 9.89-9.88 2.64 0 5.12 1.03 6.99 2.9a9.82 9.82 0 0 1 2.89 6.99c0 5.45-4.44 9.88-9.88 9.88m8.41-18.3A11.82 11.82 0 0 0 12.05 0C5.5 0 .16 5.34.16 11.89c0 2.1.55 4.14 1.59 5.95L.06 24l6.3-1.65a11.88 11.88 0 0 0 5.68 1.45h.01c6.55 0 11.89-5.34 11.89-11.89a11.82 11.82 0 0 0-3.48-8.41" />
  </svg>
);

export const WhatsAppButton = ({ className = "", testid = "whatsapp-consultant-btn" }) => (
  <a
    href={BRAND.wa}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className={`group relative flex items-center justify-center gap-3 h-11 rounded-xl border border-emerald-200 bg-emerald-50/40 px-5 text-sm font-semibold text-emerald-700 transition-[transform,box-shadow,background-color,border-color] duration-300 hover:-translate-y-0.5 hover:bg-emerald-50 hover:border-emerald-300 hover:shadow-[0_12px_28px_-14px_rgba(16,185,129,0.65)] active:scale-[0.98] ${className}`}
  >
    <span className="wa-pulse grid place-items-center h-7 w-7 rounded-full bg-[var(--wa)] text-white transition-transform duration-300 group-hover:-translate-x-0.5 group-hover:rotate-[-8deg]">
      <WaIcon className="h-4 w-4" />
    </span>
    Eğitim Danışmanına Bağlan
  </a>
);

export function EmptyState({ icon: Icon, title, desc, testid = "empty-state", children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: EASE }}
      className="surface flex flex-col items-center text-center px-6 py-14"
      data-testid={testid}
    >
      <motion.div
        animate={{ y: [0, -6, 0] }}
        transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
        className="grid place-items-center h-16 w-16 rounded-2xl bg-[var(--brand-50)] text-[var(--brand)] mb-5 ring-8 ring-[var(--brand-50)]/50"
      >
        <Icon className="h-7 w-7" strokeWidth={1.6} />
      </motion.div>
      <h3 className="text-base font-bold text-[var(--ink)]">{title}</h3>
      {desc && <p className="mt-1.5 max-w-sm text-sm text-[var(--muted-ink)]">{desc}</p>}
      {children}
    </motion.div>
  );
}

export const ErrorState = ({ message, onRetry }) => (
  <div className="surface flex flex-col items-center text-center px-6 py-12" data-testid="error-state">
    <AlertTriangle className="h-8 w-8 text-amber-500 mb-3" strokeWidth={1.6} />
    <p className="text-sm text-[var(--ink-2)]">{message}</p>
    {onRetry && (
      <button onClick={onRetry} className="btn-ghost mt-4 inline-flex items-center gap-2 rounded-xl px-4 h-10 text-sm font-semibold" data-testid="error-retry-btn">
        <RotateCw className="h-4 w-4" /> Tekrar Dene
      </button>
    )}
  </div>
);

export const SkeletonGrid = ({ count = 3, className = "h-64", cols = "sm:grid-cols-2 xl:grid-cols-3" }) => (
  <div className={`grid gap-5 ${cols}`} data-testid="skeleton-grid">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className={`skeleton ${className}`} />
    ))}
  </div>
);

const BADGES = {
  active: ["Aktif", "bg-emerald-50 text-emerald-700 ring-emerald-200"],
  passive: ["Pasif", "bg-slate-100 text-slate-600 ring-slate-200"],
  completed: ["Tamamlandı", "bg-emerald-50 text-emerald-700 ring-emerald-200"],
  in_progress: ["Devam Ediyor", "bg-amber-50 text-amber-700 ring-amber-200"],
  passed: ["Başarılı", "bg-emerald-50 text-emerald-700 ring-emerald-200"],
  failed: ["Başarısız", "bg-rose-50 text-rose-700 ring-rose-200"],
  pending: ["Bekliyor", "bg-amber-50 text-amber-700 ring-amber-200"],
  demo: ["Demo", "bg-white/90 text-slate-700 ring-slate-200"],
};

export const Badge = ({ status, className = "", testid }) => {
  const [label, cls] = BADGES[status] || [status, "bg-slate-100 text-slate-600 ring-slate-200"];
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ${cls} ${className}`}>
      {label}
    </span>
  );
};

export const PageHeader = ({ eyebrow, title, desc, action }) => (
  <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
    <div>
      {eyebrow && <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[var(--brand)] mb-2">{eyebrow}</p>}
      <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[var(--ink)]" data-testid="page-title">
        {title}
      </h1>
      {desc && <p className="mt-1.5 text-sm sm:text-base text-[var(--muted-ink)]">{desc}</p>}
    </div>
    {action}
  </div>
);

export const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toLocaleUpperCase("tr-TR");

export function AuthBackground() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      <div className="grid-bg absolute inset-0" />
      <div className="orb anim-float-a h-[420px] w-[420px] -top-24 -left-24" style={{ background: "rgba(227,10,23,0.35)" }} />
      <div className="orb anim-float-b h-[380px] w-[380px] top-1/3 right-[-120px]" style={{ background: "rgba(99,102,241,0.25)" }} />
      <div className="orb anim-float-a h-[300px] w-[300px] bottom-[-120px] left-1/3" style={{ background: "rgba(251,146,60,0.25)" }} />
      <div className="anim-spin-slow absolute top-[12%] right-[38%] h-40 w-40 rounded-full border border-dashed border-slate-300/70" />
      <div className="anim-drift absolute bottom-[18%] left-[8%] h-16 w-16 rounded-2xl border border-[rgba(227,10,23,0.25)] bg-white/40 backdrop-blur" />
      <div className="anim-drift absolute top-[20%] left-[44%] h-6 w-6 rotate-45 rounded-md bg-[rgba(227,10,23,0.18)]" style={{ animationDelay: "-3s" }} />
      <div className="anim-float-b absolute bottom-[30%] right-[8%] h-3 w-3 rounded-full bg-[var(--ink)]/20" />
    </div>
  );
}

export const DevCredit = () => (
  <p className="text-center text-[11px] text-slate-400" data-testid="developer-credit">
    <a href="https://cukurovadijitalajans.com/" target="_blank" rel="noopener noreferrer" className="link-reveal font-medium text-slate-500 hover:text-[var(--ink)]" data-testid="developer-credit-link">
      Çukurova Dijital Ajans
    </a>{" "}
    tarafından geliştirildi
  </p>
);
